import { useCallback } from 'react';
import { updateCellInRows } from '../utils/arrayUtils';
import { handleCellValueChange, handlePasteData } from '../utils/inputUtils';

/**
 * Custom hook for handling cell interactions (change, blur, paste)
 * @param {Array} rows - Current table rows
 * @param {Function} setRows - Function to update rows
 * @param {Function} setChanges - Function to update changes state
 * @param {boolean} splitMode - Whether split mode is enabled
 * @param {Array} fixedNetworkAdjustmentFactors - Fixed factors array
 * @param {Object} tempValues - Temporary values object
 * @param {Function} updateTempValue - Function to update temp value
 * @param {Function} clearTempValue - Function to clear temp value
 * @param {string} title - Row title
 * @returns {Object} - Object with interaction handlers
 */
export function useCellInteractions(
  rows, 
  setRows, 
  setChanges, 
  splitMode, 
  fixedNetworkAdjustmentFactors,
  tempValues,
  updateTempValue,
  clearTempValue,
  title
) {
  const handleCellChange = useCallback((originalIndex, colIndex, value, part) => {
    const result = handleCellValueChange(originalIndex, colIndex, value, part, rows, splitMode, fixedNetworkAdjustmentFactors, title);

    if (result.isValid) {
      const tempKey = `${originalIndex}-${colIndex}-${part}`;
      updateTempValue(tempKey, value);
    }
  }, [rows, splitMode, fixedNetworkAdjustmentFactors, updateTempValue, title]);

  const handleCellBlur = useCallback((originalIndex, colIndex, part) => {
    const tempKey = `${originalIndex}-${colIndex}-${part}`;
    const newValue = tempValues[tempKey];
    
    if (newValue !== undefined) {
      setChanges(true);
      const updatedRows = updateCellInRows(rows, originalIndex, colIndex, newValue, part, splitMode);
      setRows(updatedRows);
      clearTempValue(tempKey);
    }
  }, [tempValues, rows, splitMode, setRows, setChanges, clearTempValue]);

  const handlePaste = useCallback((rowIndex, colIndex, event) => {
    setChanges(true);
    const updatedRows = handlePasteData(rowIndex, colIndex, event, rows, splitMode, fixedNetworkAdjustmentFactors);
    setRows(updatedRows);
  }, [rows, splitMode, fixedNetworkAdjustmentFactors, setRows, setChanges]);

  return {
    handleCellChange,
    handleCellBlur,
    handlePaste
  };
}
